import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Input, Modal, Space, Table, Tag, Tooltip, App } from 'antd';
import { ScanDetailsModal } from '../components/ScanDetailsModal.js';
// import { getAccessToken } from './oidc.js';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { apiGet, apiPost } from '../api.js';

type ScanRow = {
  id: string;
  siteKey: string;
  topDomain: string;
  url: string;
  finalUrl: string;
  gpc: boolean;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  status: string;
  summary?: { newThirdPartyCount?: number; cookies?: { newNameCount?: number } };
  analysis?: unknown;
};

type DailyRow = {
  day: string;
  runs: number;
  new_third_parties: number;
  new_cookie_names: number;
  ok_runs: number;
};
const ScansDailyChart = lazy(() => import('../components/ScansDailyChart.js'));

type RedirectHop = { from: string; to: string; status?: number };
type ScanDetail = {
  id: string;
  url: string;
  finalUrl: string;
  startedAt: string;
  durationMs: number;
  status: string;
  gpc: boolean;
  redirectChain?: RedirectHop[];
  analysis?: {
    headers?: { missing?: string[]; issues?: string[] };
    cookies?: { issues?: string[] };
  };
  diff?: {
    newThirdPartyHosts?: string[];
    blockedUntilConsent?: Array<{ host: string; category?: string }>;
    cookies?: {
      added?: Array<{ name: string; domain?: string; expires?: string | null }>;
      removed?: Array<{ name: string; domain?: string }>;
      changed?: Array<{ name: string; domain?: string; from?: string; to?: string }>;
    };
  };
};

export function ScansPage({ registryUrl }: { registryUrl: string }) {
  const { message } = App.useApp();
  const [siteKey, setSiteKey] = useState<string>(
    () => localStorage.getItem('cmp.portal.lastSiteKey') || 'DEV_SITE_KEY',
  );
  const [from, setFrom] = useState<string>(() => dayjs().subtract(30, 'day').toISOString());
  const [to, setTo] = useState<string>(() => dayjs().toISOString());
  const [rows, setRows] = useState<ScanRow[]>([]);
  const [daily, setDaily] = useState<DailyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState<'runs' | 'new_third_parties' | 'new_cookie_names'>('runs');
  const [runUrl, setRunUrl] = useState<string>('https://example.com');
  const [runGpc, setRunGpc] = useState<boolean>(false);
  const [scanApiBase, setScanApiBase] = useState<string>('http://localhost:3006');
  const [scanApiStatus, setScanApiStatus] = useState<string>('Unknown');
  const [scanApiProxyStatus, setScanApiProxyStatus] = useState<string>('Unknown');
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [detail, setDetail] = useState<ScanDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [statusOpen, setStatusOpen] = useState<boolean>(false);
  const [jobStatus, setJobStatus] = useState<null | {
    jobId: string;
    state: 'queued' | 'running' | 'done' | 'failed' | 'not_found' | string;
    attemptsMade?: number;
    error?: string;
  }>(null);
  // Failed jobs (BullMQ-only; empty in DB mode)
  type FailedJob = {
    id: string;
    reason?: string;
    attempts?: number;
    data?: { siteKey?: string; url?: string };
  };
  const [failedRows, setFailedRows] = useState<FailedJob[]>([]);
  const [failedLoading, setFailedLoading] = useState(false);
  const [failedPage, setFailedPage] = useState(0);
  const [failedSize, setFailedSize] = useState(10);

  const load = async () => {
    setLoading(true);
    try {
      const listUrl = new URL(`${registryUrl}/admin/scans`);
      if (siteKey) listUrl.searchParams.set('siteKey', siteKey);
      listUrl.searchParams.set('take', '50');
      const list = await apiGet<ScanRow[]>(listUrl.toString());
      setRows(list);

      const sumUrl = new URL(`${registryUrl}/admin/scans/summary/daily`);
      if (siteKey) sumUrl.searchParams.set('siteKey', siteKey);
      if (from) sumUrl.searchParams.set('from', from);
      if (to) sumUrl.searchParams.set('to', to);
      const s = await apiGet<DailyRow[]>(sumUrl.toString());
      setDaily(s);
    } catch {
      setRows([]);
      setDaily([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFailed = async (page = failedPage, size = failedSize) => {
    setFailedLoading(true);
    try {
      const url = new URL(`${registryUrl}/admin/scans/failed`);
      url.searchParams.set('page', String(page));
      url.searchParams.set('size', String(size));
      type RespEnvelope = { items: FailedJob[]; total?: number; page?: number; size?: number };
      const r: FailedJob[] | RespEnvelope = await apiGet(url.toString());
      const items = Array.isArray(r) ? r : ((r as RespEnvelope).items ?? []);
      setFailedRows(items);
      if (!Array.isArray(r) && typeof r.total === 'number') {
        setFailedTotal(r.total as number);
      }
    } catch {
      setFailedRows([]);
    } finally {
      setFailedLoading(false);
    }
  };
  const [failedTotal, setFailedTotal] = useState(0);
  const [queueCounts, setQueueCounts] = useState<{
    waiting?: number;
    active?: number;
    completed?: number;
    failed?: number;
    delayed?: number;
    paused?: number;
  } | null>(null);

  useEffect(() => {
    load().catch(() => undefined);
    loadFailed().catch(() => undefined);
    // BullMQ health (best-effort)
    (async () => {
      try {
        const url = new URL(`${registryUrl}/admin/scans/queue/health`);
        const j = await apiGet<
          | {
              waiting?: number;
              active?: number;
              completed?: number;
              failed?: number;
              delayed?: number;
              paused?: number;
            }
          | { mode: string }
        >(url.toString());
        if ('waiting' in j || 'active' in j || 'failed' in j)
          setQueueCounts(
            j as {
              waiting?: number;
              active?: number;
              completed?: number;
              failed?: number;
              delayed?: number;
              paused?: number;
            },
          );
        else setQueueCounts(null);
      } catch {
        setQueueCounts(null);
      }
    })().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryUrl]);

  const triggerScan = async () => {
    try {
      setLoading(true);
      await apiPost(`${registryUrl}/admin/scans/run`, {
        url: runUrl,
        siteKey,
        gpc: runGpc,
        scanApiBase,
        registryBase: registryUrl,
      });
      message.success('Scan started and persisted');
      await load();
    } catch (e) {
      message.error(`Scan failed: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const enqueueScan = async () => {
    try {
      const { jobId } = await apiPost<{ jobId: string }>(`${registryUrl}/admin/scans/enqueue`, {
        url: runUrl,
        siteKey,
        gpc: runGpc,
        scanApiBase,
        registryBase: registryUrl,
      });
      message.success('Enqueued');
      // show status modal while polling
      setStatusOpen(true);
      setJobStatus({ jobId, state: 'queued', attemptsMade: 0 });
      // Poll until done
      let tries = 60; // ~60 * 2s = 2 minutes
      while (tries-- > 0) {
        const st = await apiGet<{
          state: 'queued' | 'running' | 'done' | 'failed' | 'not_found' | string;
          attemptsMade?: number;
          error?: string;
        }>(`${registryUrl}/admin/scans/status/${encodeURIComponent(jobId)}`);
        setJobStatus({ jobId, state: st.state, attemptsMade: st.attemptsMade, error: st.error });
        if (st.state === 'done' || st.state === 'failed') break;
        // sleep 2s
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 2000));
      }
      await load();
      await loadFailed();
    } catch (e) {
      message.error(`Enqueue failed: ${(e as Error).message}`);
      setStatusOpen(false);
      setJobStatus(null);
    }
  };

  // requeue helper is referenced by status UI (future enhancement)
  const requeue = React.useCallback(
    async (jobId: string) => {
      try {
        await apiPost(`${registryUrl}/admin/scans/requeue/${encodeURIComponent(jobId)}`, {});
        message.success('Requeued');
        setStatusOpen(false);
        setJobStatus(null);
      } catch (e) {
        message.error(`Requeue failed: ${(e as Error).message}`);
      }
    },
    [registryUrl, message],
  );

  const columns: ColumnsType<ScanRow> = useMemo(
    () => [
      {
        title: 'Started',
        dataIndex: 'startedAt',
        key: 'startedAt',
        render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        title: 'GPC',
        dataIndex: 'gpc',
        key: 'gpc',
        width: 80,
        render: (v: boolean) => (v ? <Tag color="purple">GPC</Tag> : '—'),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (v: string) =>
          v === 'ok' ? <Tag color="green">ok</Tag> : <Tag color="red">{v || 'error'}</Tag>,
      },
      {
        title: 'Duration',
        dataIndex: 'durationMs',
        key: 'durationMs',
        width: 100,
        render: (v: number) => `${v} ms`,
      },
      {
        title: 'Final URL',
        dataIndex: 'finalUrl',
        key: 'finalUrl',
        render: (v: string) => (
          <Tooltip title={v}>
            <span
              style={{
                maxWidth: 320,
                display: 'inline-block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                verticalAlign: 'middle',
              }}
            >
              {v}
            </span>
          </Tooltip>
        ),
      },
      {
        title: 'New 3P',
        key: 'new3p',
        width: 90,
        align: 'center',
        render: (_: unknown, r: ScanRow) => r.summary?.newThirdPartyCount ?? 0,
      },
      {
        title: 'New cookies',
        key: 'newcookies',
        width: 110,
        align: 'center',
        render: (_: unknown, r: ScanRow) => r.summary?.cookies?.newNameCount ?? 0,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 110,
        render: (_: unknown, r: ScanRow) => (
          <Button size="small" onClick={() => void loadDetail(r.id)}>
            View
          </Button>
        ),
      },
    ],
    [loadDetail],
  );

  // Removed old inline bar max calculation (chart handles scaling)

  const checkScanApi = async () => {
    try {
      setScanApiStatus('Checking…');
      const base = scanApiBase.replace(/\/$/, '');
      const fetchWithTimeout = async (url: string, ms = 5000) => {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), ms);
        try {
          return await fetch(url, { signal: ctrl.signal, cache: 'no-store' });
        } finally {
          clearTimeout(t);
        }
      };
      // First health
      // eslint-disable-next-line no-console
      if (new URLSearchParams(window.location.search).get('debug') === '1')
        console.log('[ScansPage] GET', `${base}/health`);
      const r = await fetchWithTimeout(`${base}/health`, 5000);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = (await r.json()) as { ok?: boolean };
      if (!json?.ok) {
        setScanApiStatus('Unavailable');
        return;
      }
      // Then readiness (best-effort)
      try {
        // eslint-disable-next-line no-console
        if (new URLSearchParams(window.location.search).get('debug') === '1')
          console.log('[ScansPage] GET', `${base}/health/ready`);
        const rr = await fetchWithTimeout(`${base}/health/ready`, 3000);
        if (rr.ok) {
          const jj = (await rr.json()) as { ok?: boolean };
          setScanApiStatus(jj?.ok ? 'Ready' : 'OK');
        } else {
          setScanApiStatus('OK');
        }
      } catch {
        setScanApiStatus('OK');
      }
    } catch {
      setScanApiStatus('Unavailable');
    }
  };

  const checkScanApiViaRegistry = async () => {
    try {
      setScanApiProxyStatus('Checking…');
      const url = `${registryUrl}/health/scan-api`;
      const fetchWithTimeout = async (u: string, ms = 7000) => {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), ms);
        try {
          return await fetch(u, {
            signal: ctrl.signal,
            cache: 'no-store',
          });
        } finally {
          clearTimeout(t);
        }
      };
      if (new URLSearchParams(window.location.search).get('debug') === '1')
        // eslint-disable-next-line no-console
        console.log('[ScansPage] GET (proxy)', url);
      const r = await fetchWithTimeout(url, 7000);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = (await r.json()) as { ok?: boolean };
      setScanApiProxyStatus(j?.ok ? 'Ready' : 'Unavailable');
    } catch {
      setScanApiProxyStatus('Unavailable');
    }
  };

  const loadDetail = React.useCallback(
    async (id: string) => {
      try {
        setDetailOpen(true);
        setDetailLoading(true);
        const url = `${registryUrl}/admin/scans/${encodeURIComponent(id)}`;
        const j = await apiGet<ScanDetail>(url);
        setDetail(j);
      } catch (e) {
        message.error(`Failed to load details: ${(e as Error).message}`);
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [registryUrl, message],
  );

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          style={{ width: 300 }}
          placeholder="Site key"
          value={siteKey}
          onChange={(e) => setSiteKey(e.target.value)}
        />
        <DatePicker
          value={dayjs(from)}
          onChange={(d) => setFrom((d ?? dayjs().subtract(30, 'day')).toISOString())}
        />
        <DatePicker value={dayjs(to)} onChange={(d) => setTo((d ?? dayjs()).toISOString())} />
        <Space.Compact>
          <Button
            onClick={() => {
              setTo(dayjs().toISOString());
              setFrom(dayjs().subtract(7, 'day').toISOString());
            }}
          >
            7d
          </Button>
          <Button
            onClick={() => {
              setTo(dayjs().toISOString());
              setFrom(dayjs().subtract(30, 'day').toISOString());
            }}
          >
            30d
          </Button>
          <Button
            onClick={() => {
              setTo(dayjs().toISOString());
              setFrom(dayjs().subtract(90, 'day').toISOString());
            }}
          >
            90d
          </Button>
        </Space.Compact>
        {queueCounts && (
          <span
            title="BullMQ queue health"
            style={{
              fontSize: 12,
              color: '#666',
              border: '1px solid #ddd',
              borderRadius: 6,
              padding: '2px 6px',
            }}
          >
            Q w {queueCounts.waiting ?? 0} · a {queueCounts.active ?? 0} · f{' '}
            {queueCounts.failed ?? 0}
          </span>
        )}
        <Space.Compact>
          <Button
            onClick={() => setMetric('runs')}
            type={metric === 'runs' ? 'primary' : 'default'}
          >
            Runs
          </Button>
          <Button
            onClick={() => setMetric('new_third_parties')}
            type={metric === 'new_third_parties' ? 'primary' : 'default'}
          >
            New 3P
          </Button>
          <Button
            onClick={() => setMetric('new_cookie_names')}
            type={metric === 'new_cookie_names' ? 'primary' : 'default'}
          >
            New cookies
          </Button>
        </Space.Compact>
        {queueCounts && (
          <span
            title="BullMQ queue health"
            style={{
              fontSize: 12,
              color: '#666',
              border: '1px solid #ddd',
              borderRadius: 6,
              padding: '2px 6px',
            }}
          >
            Q w {queueCounts.waiting ?? 0} · a {queueCounts.active ?? 0} · c{' '}
            {queueCounts.completed ?? 0} · f {queueCounts.failed ?? 0} · d{' '}
            {queueCounts.delayed ?? 0} · p {queueCounts.paused ?? 0}
          </span>
        )}
        <Button type="primary" onClick={load} loading={loading}>
          Refresh
        </Button>
      </div>

      <section style={{ border: '1px solid #eee', borderRadius: 8, padding: 12 }}>
        <h3 style={{ marginTop: 0 }}>Run scan</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Input
            style={{ width: 360 }}
            value={runUrl}
            onChange={(e) => setRunUrl(e.target.value)}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={runGpc} onChange={(e) => setRunGpc(e.target.checked)} />
            GPC
          </label>
          <Input
            style={{ width: 260 }}
            addonBefore="Scan API"
            value={scanApiBase}
            onChange={(e) => setScanApiBase(e.target.value)}
          />
          <Button onClick={checkScanApi}>Check</Button>
          <Tag
            color={
              scanApiStatus === 'Ready' || scanApiStatus === 'OK'
                ? 'green'
                : scanApiStatus === 'Checking…'
                  ? 'blue'
                  : 'red'
            }
          >
            {scanApiStatus}
          </Tag>
          <Button onClick={checkScanApiViaRegistry}>Check via Registry</Button>
          <Tag
            color={
              scanApiProxyStatus === 'Ready'
                ? 'green'
                : scanApiProxyStatus === 'Checking…'
                  ? 'blue'
                  : 'red'
            }
          >
            {scanApiProxyStatus}
          </Tag>
          <Space.Compact>
            <Button onClick={enqueueScan}>Scan now (enqueue)</Button>
            <Button type="primary" onClick={triggerScan} loading={loading}>
              Run scan (direct)
            </Button>
          </Space.Compact>
        </div>
      </section>

      <section style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, marginTop: 12 }}>
        <h3 style={{ marginTop: 0 }}>Daily summary</h3>
        <div style={{ height: 220 }}>
          <Suspense fallback={<div className="text-xs text-gray-500">Loading scans chart…</div>}>
            <ScansDailyChart data={daily} />
          </Suspense>
        </div>
      </section>

      <Table<ScanRow>
        rowKey="id"
        dataSource={rows}
        columns={columns}
        pagination={false}
        size="middle"
      />

      {/* Failed jobs (BullMQ mode); shows empty in DB mode */}
      <FailedJobsTable
        rows={failedRows}
        loading={failedLoading}
        page={failedPage}
        size={failedSize}
        total={failedTotal}
        onReload={() => void loadFailed()}
        onPageChange={(newPage, newSize) => {
          setFailedPage(newPage);
          setFailedSize(newSize);
          void loadFailed(newPage, newSize);
        }}
        onRequeue={(id) => void requeue(id)}
      />
      <ScanDetailsModal
        open={detailOpen}
        loading={detailLoading}
        detail={detail}
        onClose={() => setDetailOpen(false)}
      />
      <Modal
        title="Enqueued scan status"
        open={statusOpen}
        onCancel={() => setStatusOpen(false)}
        footer={null}
        width={520}
      >
        {!jobStatus ? (
          <div>Waiting…</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            <div>
              <strong>Job ID:</strong> <code>{jobStatus.jobId}</code>
            </div>
            <div>
              <strong>State:</strong>{' '}
              {jobStatus.state === 'done' ? (
                <Tag color="green">done</Tag>
              ) : jobStatus.state === 'failed' ? (
                <Tag color="red">failed</Tag>
              ) : (
                <Tag>{jobStatus.state}</Tag>
              )}
            </div>
            {typeof jobStatus.attemptsMade === 'number' && (
              <div>
                <strong>Retries:</strong> {jobStatus.attemptsMade}
              </div>
            )}
            {jobStatus.error && (
              <div style={{ color: '#a00' }}>
                <strong>Error:</strong> {jobStatus.error}
              </div>
            )}
            {jobStatus.state === 'failed' && (
              <div>
                <Button onClick={() => requeue(jobStatus.jobId)} type="primary">
                  Requeue
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
